import { useQuery } from "@tanstack/react-query";

/* The browser button, EVI Capture, and which version of it is where.
 *
 * deploy/deploy_app.sh builds the zip from buzze-capture's last commit and
 * writes the version beside it. The extension writes its own version onto
 * every sales.buzze.biz page it loads (announce.js, from 0.3.0). A page with no
 * attribute has no extension, or one from before 0.3.0, and gets no banner. */

export const CAPTURE_ZIP = "/downloads/evie-capture.zip";
const CAPTURE_VERSION_FILE = "/downloads/evi-capture.json";
const DISMISSED_KEY = "saleshive.capture-banner-dismissed";

export function installedCaptureVersion(): string | null {
  return document.documentElement.dataset.eviCapture ?? null;
}

/* True when `offered` is later than `installed`, number by number, so 0.10.0
   is later than 0.9.0. The extension's status.js has the same rule. A missing
   or unreadable version is never later, so a bad file shows no banner. */
export function isNewer(offered: string | null | undefined, installed: string | null | undefined): boolean {
  const parts = (v: string | null | undefined) =>
    typeof v === "string" && /^\d+(\.\d+)*$/.test(v) ? v.split(".").map(Number) : null;
  const a = parts(offered);
  const b = parts(installed);
  if (!a || !b) return false;
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}

/* The version on offer. Read once per session. Caddy serves it no-cache, so a
   new deploy reaches the next page load. */
export function useOfferedCaptureVersion() {
  return useQuery({
    queryKey: ["capture-version"],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      const res = await fetch(CAPTURE_VERSION_FILE, { cache: "no-store" });
      if (!res.ok) throw new Error(`the version file returned ${res.status}`);
      // An address with no file behind it gets the app's own page back from
      // Caddy, with a 200, so the type is checked before the body is read.
      if (!(res.headers.get("content-type") ?? "").includes("json")) {
        throw new Error("the version file is missing from this deploy");
      }
      const body = (await res.json()) as { version?: unknown };
      if (typeof body.version !== "string") throw new Error("the version file has no version");
      return body.version;
    },
  });
}

/* The banner is dismissed per version, so the next release shows it again.
   Browser storage can be missing or refuse, and then the banner shows. */
export function dismissedCaptureVersion(): string | null {
  try { return window.localStorage.getItem(DISMISSED_KEY); } catch { return null; }
}

export function dismissCaptureVersion(version: string) {
  try { window.localStorage.setItem(DISMISSED_KEY, version); } catch { /* shows again next load */ }
}
